import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Toaster, toast } from "react-hot-toast";
import {
  UpdateTenant,
  UpdateTenantRequest,
  Tenant,
  GetAllTenants,
} from "../Services/APIService";
import OverlayLoader from "./OverlayLoader";

interface EditTenantModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void;
  tenantData: Tenant | null;
}

interface FormData {
  tenantName: string;
  tenantAbbreviation: string;
  address1: string;
  address2: string;
  website: string;
  phone1: string;
  email1: string;
  databaseConnection: string;
  isActive: boolean;
  isUseOwnEmail: boolean;
  isUseOwnWp: boolean;
  isUseOwnSMS: boolean;
}

interface ValidationErrors {
  tenantName?: string;
  tenantAbbreviation?: string;
  address1?: string;
  address2?: string;
  website?: string;
  phone1?: string;
  email1?: string;
  databaseConnection?: string;
}

const EditTenantModal: React.FC<EditTenantModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  tenantData,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [formData, setFormData] = useState<FormData>({
    tenantName: "",
    tenantAbbreviation: "",
    address1: "",
    address2: "",
    website: "",
    phone1: "",
    email1: "",
    databaseConnection: "",
    isActive: true,
    isUseOwnEmail: false,
    isUseOwnWp: false,
    isUseOwnSMS: false,
  });
  const navigate = useNavigate();

  useEffect(() => {
    if (tenantData && isOpen) {
      setFormData({
        tenantName: tenantData.tenanT_NAME || "",
        tenantAbbreviation: tenantData.tenanT_ABREVATION || "",
        address1: tenantData.addresS1 || "",
        address2: tenantData.addresS2 || "",
        website: tenantData.website || "",
        phone1: tenantData.phone?.toString() || "",
        email1: tenantData.email?.toString() || "",
        databaseConnection: tenantData.databasE_CONNECTION || "",
        isActive: tenantData.isactive || false,
        // 🔹 Bind booleans from row
        isUseOwnEmail: tenantData.isUseOwnEmail ?? false,
        isUseOwnWp: tenantData.isUseOwnWp ?? false,
        isUseOwnSMS: tenantData.isUseOwnSMS ?? false,
      });
    }
  }, [tenantData, isOpen]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (value && errors[name as keyof ValidationErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(
      (prev) =>
        ({
          ...prev,
          [name]: checked,
        } as FormData)
    );
  };

  const getLocalStorageValue = (key: string): number => {
    const value = localStorage.getItem(key);
    return value ? parseInt(value, 10) : 0;
  };

  type ValidateOptions = {
    excludeAbbreviation?: string; // pass current tenant abbreviation when editing
  };

  const validateForm = async (
    options?: ValidateOptions
  ): Promise<{ isValid: boolean; errors: ValidationErrors }> => {
    const newErrors: ValidationErrors = {};
    let isValid = true;

    // fetch tenants
    const tenantsResponse = await GetAllTenants();

    const existingAbbreviations =
      tenantsResponse?.map((t: any) =>
        (t.tenanT_ABREVATION || "").toLowerCase()
      ) || [];

    // exclude current abbreviation when editing
    const exclude = options?.excludeAbbreviation?.toLowerCase();
    const filteredAbbreviations = exclude
      ? existingAbbreviations.filter((a: string) => a !== exclude)
      : existingAbbreviations;

    // Tenant Name
    if (!formData.tenantName.trim()) {
      newErrors.tenantName = "Tenant name is required";
      isValid = false;
    }

    // Abbreviation
    const abbr = formData.tenantAbbreviation.trim();

    if (!abbr) {
      newErrors.tenantAbbreviation = "Abbreviation is required";
      isValid = false;
    } else {
      if (abbr.length !== 3) {
        newErrors.tenantAbbreviation =
          "Abbreviation must be exactly 3 characters";
        isValid = false;
      } else if (filteredAbbreviations.includes(abbr.toLowerCase())) {
        newErrors.tenantAbbreviation = "Abbreviation already exists";
        isValid = false;
      }
    }

    // Address
    if (!formData.address1.trim()) {
      newErrors.address1 = "Address is required";
      isValid = false;
    }

    // Phone
    if (!formData.phone1.trim()) {
      newErrors.phone1 = "Phone is required";
      isValid = false;
    }

    // Email
    if (!formData.email1.trim()) {
      newErrors.email1 = "Email is required";
      isValid = false;
    }

    setErrors(newErrors);

    return { isValid, errors: newErrors };
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      const validation = await validateForm({
        excludeAbbreviation: tenantData?.tenanT_ABREVATION,
      });
      if (!validation.isValid) {
        setIsSubmitting(false);
        return;
      }

      if (!tenantData) {
        toast.error("Tenant data not found");
        setIsSubmitting(false);
        return;
      }

      const modifiedBy = getLocalStorageValue("superAdminId");
      const now = new Date().toISOString();

      const requestData: UpdateTenantRequest = {
        tenanT_ID: tenantData.tenanT_ID,
        tenanT_NAME: formData.tenantName,
        tenanT_ABREVATION: formData.tenantAbbreviation,
        address1: formData.address1, // 🔹 match API format
        addresS2: formData.address2,
        phone: formData.phone1,
        email: formData.email1,
        website: formData.website,
        isactive: formData.isActive,
        databasE_CONNECTION: formData.databaseConnection,
        modifieD_BY: modifiedBy,
        modifieD_ON: now,
        // 🔹 send checkbox values
        isUseOwnEmail: formData.isUseOwnEmail,
        isUseOwnWp: formData.isUseOwnWp,
        isUseOwnSMS: formData.isUseOwnSMS,
      };

      const result = await UpdateTenant(requestData);
      console.log("Tenant updated successfully:", result);

      toast.success("Tenant updated successfully!");

      setFormData({
        tenantName: "",
        tenantAbbreviation: "",
        address1: "",
        address2: "",
        website: "",
        phone1: "",
        email1: "",
        databaseConnection: "",
        isActive: true,
        isUseOwnEmail: false,
        isUseOwnWp: false,
        isUseOwnSMS: false,
      });

      onSubmit();
      onClose();
    } catch (error) {
      console.error("Error updating tenant:", error);
      toast.error("Error updating tenant. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      tenantName: "",
      tenantAbbreviation: "",
      address1: "",
      address2: "",
      website: "",
      phone1: "",
      email1: "",
      databaseConnection: "",
      isActive: true,
      isUseOwnEmail: false,
      isUseOwnWp: false,
      isUseOwnSMS: false,
    });
    setErrors({});
    onClose();
  };

  const requiredField = <span className="text-danger ms-1">*</span>;

  return (
    <>
      <div>
        <Toaster position="top-center" reverseOrder={false} />
      </div>

      <div
        className={`modal-backdrop-custom ${isOpen ? "open" : "closed"}`}
        onClick={handleClose}
      ></div>

      <div className={`modal-custom ${isOpen ? "open" : "closed"}`}>
        <div className="modal-content usertaskCreatFormContent">
          <div className="modal-header-custom">
            <h5 className="modal-title-custom">Edit Tenant</h5>
            <button
              type="button"
              className="btn-close-custom"
              onClick={handleClose}
            >
              <span className="icon-close"></span>
            </button>
          </div>

          <div className="modal-body-custom">
            <div className="card pop-up-card">
              <div className="card-body p-0 overflow-hidden">
                <div className="modal-body-content">
                  <form>
                    <div className="row">
                      <div className="col-12 col-lg-6 mb-3 input-group-custom">
                        <label htmlFor="tenantName" className="form-label">
                          Tenant Name{requiredField}
                        </label>
                        <div className="input-with-icon">
                          <input
                            type="text"
                            id="tenantName"
                            name="tenantName"
                            className={`form-control-custom form-control-custom1 ${
                              errors.tenantName ? "is-invalid" : ""
                            }`}
                            placeholder="Enter tenant name"
                            value={formData.tenantName}
                            onChange={handleInputChange}
                          />
                          {errors.tenantName && (
                            <div className="invalid-feedback-custom">
                              {errors.tenantName}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="col-12 col-lg-6 mb-3 input-group-custom">
                        <label
                          htmlFor="tenantAbbreviation"
                          className="form-label"
                        >
                          Abbreviation{requiredField}
                        </label>
                        <div className="input-with-icon">
                          <input
                            type="text"
                            id="tenantAbbreviation"
                            name="tenantAbbreviation"
                            className={`form-control-custom form-control-custom1 ${
                              errors.tenantAbbreviation ? "is-invalid" : ""
                            }`}
                            placeholder="Enter abbreviation"
                            value={formData.tenantAbbreviation}
                            onChange={handleInputChange}
                          />
                          {errors.tenantAbbreviation && (
                            <div className="invalid-feedback-custom">
                              {errors.tenantAbbreviation}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="col-12 col-lg-6 mb-3 input-group-custom">
                        <label htmlFor="phone1" className="form-label">
                          Phone{requiredField}
                        </label>
                        <div className="input-with-icon">
                          <input
                            type="text"
                            id="phone1"
                            name="phone1"
                            className={`form-control-custom form-control-custom1 ${
                              errors.phone1 ? "is-invalid" : ""
                            }`}
                            placeholder="Enter phone"
                            value={formData.phone1}
                            onChange={handleInputChange}
                          />
                          {errors.phone1 && (
                            <div className="invalid-feedback-custom">
                              {errors.phone1}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="col-12 col-lg-6 mb-3 input-group-custom">
                        <label htmlFor="website" className="form-label">
                          Website
                        </label>
                        <div className="input-with-icon">
                          <input
                            type="text"
                            id="website"
                            name="website"
                            className={`form-control-custom form-control-custom1 ${
                              errors.website ? "is-invalid" : ""
                            }`}
                            placeholder="Enter website"
                            value={formData.website}
                            onChange={handleInputChange}
                          />
                          {errors.website && (
                            <div className="invalid-feedback-custom">
                              {errors.website}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="col-12 col-lg-12 mb-3 input-group-custom">
                        <label htmlFor="email1" className="form-label">
                          Email{requiredField}
                        </label>
                        <div className="input-with-icon">
                          <input
                            type="email"
                            id="email1"
                            name="email1"
                            className={`form-control-custom form-control-custom1 ${
                              errors.email1 ? "is-invalid" : ""
                            }`}
                            placeholder="Enter email"
                            value={formData.email1}
                            onChange={handleInputChange}
                          />
                          {errors.email1 && (
                            <div className="invalid-feedback-custom">
                              {errors.email1}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="col-12 col-lg-6 mb-3 input-group-custom">
                        <label htmlFor="address1" className="form-label">
                          Address{requiredField}
                        </label>
                        <div className="input-with-icon">
                          <textarea
                            onChange={handleInputChange}
                            id="address1"
                            name="address1"
                            className={`form-control-custom-textarea ${
                              errors.address1 ? "is-invalid" : ""
                            }`}
                            placeholder="Enter address"
                            value={formData.address1}
                            rows={2}
                          />
                          {errors.address1 && (
                            <div className="invalid-feedback-custom">
                              {errors.address1}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="col-12 col-lg-6 mb-3 input-group-custom">
                        <label htmlFor="address2" className="form-label">
                          2nd Address
                        </label>
                        <div className="input-with-icon">
                          <textarea
                            onChange={handleInputChange}
                            id="address2"
                            name="address2"
                            className={`form-control-custom-textarea ${
                              errors.address2 ? "is-invalid" : ""
                            }`}
                            placeholder="Enter address"
                            value={formData.address2}
                            rows={2}
                          />
                          {errors.address2 && (
                            <div className="invalid-feedback-custom">
                              {errors.address2}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="col-12 mb-3 input-group-custom">
                        <label
                          htmlFor="databaseConnection"
                          className="form-label"
                        >
                          Database Connection
                        </label>
                        <div className="input-with-icon">
                          <input
                            type="text"
                            id="databaseConnection"
                            name="databaseConnection"
                            className={`form-control-custom form-control-custom1 ${
                              errors.databaseConnection ? "is-invalid" : ""
                            }`}
                            placeholder="Enter database connection string"
                            value={formData.databaseConnection}
                            onChange={handleInputChange}
                          />
                          {errors.databaseConnection && (
                            <div className="invalid-feedback-custom">
                              {errors.databaseConnection}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Is Active */}
                      <div className="col-12 mb-2 input-group-custom">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="isActive"
                            name="isActive"
                            checked={formData.isActive}
                            onChange={handleCheckboxChange}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="isActive"
                          >
                            Is Active
                          </label>
                        </div>
                      </div>

                      {/* Use Own Email */}
                      <div className="col-12 mb-2 input-group-custom">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="isUseOwnEmail"
                            name="isUseOwnEmail"
                            checked={formData.isUseOwnEmail}
                            onChange={handleCheckboxChange}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="isUseOwnEmail"
                          >
                            Use Own Email
                          </label>
                        </div>
                      </div>

                      {/* Use Own Wp */}
                      <div className="col-12 mb-2 input-group-custom">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="isUseOwnWp"
                            name="isUseOwnWp"
                            checked={formData.isUseOwnWp}
                            onChange={handleCheckboxChange}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="isUseOwnWp"
                          >
                            Use Own Wp
                          </label>
                        </div>
                      </div>

                      {/* Use Own SMS */}
                      <div className="col-12 mb-3 input-group-custom">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="isUseOwnSMS"
                            name="isUseOwnSMS"
                            checked={formData.isUseOwnSMS}
                            onChange={handleCheckboxChange}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="isUseOwnSMS"
                          >
                            Use Own SMS
                          </label>
                        </div>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>

            <div className="modal-footer-custom">
              <div>
                <button
                  type="button"
                  onClick={handleClose}
                  className="btn-custom btn-cancel"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-custom btn-primary-custom"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Updating..." : "Update Tenant"}
                </button>
              </div>
            </div>
          </div>
        </div>
        <OverlayLoader
          loading={isLoading}
          message="Loading Tenant Data"
          submessage="This might take a moment..."
        />
      </div>
    </>
  );
};

export default EditTenantModal;
